// Excel / xlsx export for the calculation engine.
//
// Per the engineering rule "xlsx support as input/output layer only",
// this module only formats an existing `CalculationResult` as a
// workbook. No land-price rule logic lives here.

import ExcelJS from "exceljs";

import type { CalculationResult } from "../types/calculation-result.js";
import type { MarketPriceSet } from "../types/market-price.js";

const ITEM_COLUMNS: ReadonlyArray<{
  header: string;
  width: number;
  pick: (r: CalculationResult["itemResults"][number]) => unknown;
}> = [
  { header: "itemId", width: 16, pick: (r) => r.itemId },
  { header: "buildingType", width: 12, pick: (r) => r.buildingType },
  { header: "gfa", width: 10, pick: (r) => r.gfa },
  { header: "chargeableGfa", width: 14, pick: (r) => r.chargeableGfa },
  { header: "applicablePriceType", width: 18, pick: (r) => r.applicablePriceType },
  { header: "marketPrice", width: 12, pick: (r) => r.marketPrice },
  { header: "buildingTypeFactor", width: 18, pick: (r) => r.factorChain.buildingTypeFactor },
  { header: "landTermFactor", width: 16, pick: (r) => r.factorChain.landTermFactor },
  { header: "propertyConditionFactor", width: 22, pick: (r) => r.factorChain.propertyConditionFactor },
  { header: "affordableHousingFactor", width: 22, pick: (r) => r.factorChain.affordableHousingFactor },
  { header: "industryOrientationFactor", width: 24, pick: (r) => r.factorChain.industryOrientationFactor },
  { header: "industryProjectTypeFactor", width: 24, pick: (r) => r.factorChain.industryProjectTypeFactor },
  { header: "commercialFloorFactor", width: 20, pick: (r) => r.factorChain.commercialFloorFactor },
  { header: "undergroundSpaceFactor", width: 20, pick: (r) => r.factorChain.undergroundSpaceFactor },
  { header: "projectFactor", width: 14, pick: (r) => r.factorChain.projectFactor },
  { header: "finalFactor", width: 12, pick: (r) => r.factorChain.finalFactor },
  { header: "itemLandPrice", width: 14, pick: (r) => r.itemLandPrice },
  { header: "floorLandPrice", width: 14, pick: (r) => r.floorLandPrice },
  { header: "formulaText", width: 60, pick: (r) => r.formulaText },
];

const FACTOR_NAMES: ReadonlyArray<keyof CalculationResult["itemResults"][number]["factorChain"]> = [
  "marketPrice",
  "buildingTypeFactor",
  "landTermFactor",
  "propertyConditionFactor",
  "affordableHousingFactor",
  "industryOrientationFactor",
  "industryProjectTypeFactor",
  "commercialFloorFactor",
  "undergroundSpaceFactor",
  "projectFactor",
  "finalFactor",
];

const PRICE_SOURCE_ROWS: ReadonlyArray<{
  field: string;
  width: number;
  pick: (p: MarketPriceSet) => unknown;
}> = [
  { field: "priceSource", width: 24, pick: (p) => p.priceSource },
  { field: "priceDate", width: 14, pick: (p) => p.priceDate },
  { field: "marketPriceSource", width: 24, pick: (p) => p.marketPriceSource },
  { field: "marketPriceYear", width: 12, pick: (p) => p.marketPriceYear },
  { field: "valuationDate", width: 14, pick: (p) => p.valuationDate },
  { field: "effectiveDate", width: 14, pick: (p) => p.effectiveDate },
  { field: "standardParcelCode", width: 22, pick: (p) => p.standardParcelCode },
  { field: "benchmarkZoneCode", width: 22, pick: (p) => p.benchmarkZoneCode },
  { field: "officialQueryUrl", width: 60, pick: (p) => p.officialQueryUrl },
  { field: "priceSourceRemark", width: 60, pick: (p) => p.priceSourceRemark },
];

/**
 * Build an xlsx workbook for a `CalculationResult`. Returns a
 * `Uint8Array` suitable for writing to a file with `fs.writeFileSync`.
 *
 * Sheets:
 *   - "Items"        — wide form, one row per item
 *   - "FactorChains" — long form, one row per (item, factor)
 *   - "PriceSource"  — official-source metadata carried on the
 *                      input `MarketPriceSet`. Only rows whose
 *                      value is present are written.
 *   - "Summary"      — totals
 */
export async function formatItemResultsXlsx(
  result: CalculationResult,
  prices?: MarketPriceSet,
): Promise<Uint8Array> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "shenzhen-land-price-calculation";
  workbook.created = new Date();

  // ---- Items sheet ----
  const itemsSheet = workbook.addWorksheet("Items");
  itemsSheet.columns = ITEM_COLUMNS.map((c) => ({
    header: c.header,
    key: c.header,
    width: c.width,
  }));
  for (const r of result.itemResults) {
    const row: Record<string, unknown> = {};
    for (const c of ITEM_COLUMNS) {
      row[c.header] = c.pick(r);
    }
    itemsSheet.addRow(row);
  }
  itemsSheet.getRow(1).font = { bold: true };

  // ---- FactorChains sheet (long form) ----
  const chainsSheet = workbook.addWorksheet("FactorChains");
  chainsSheet.columns = [
    { header: "itemId", key: "itemId", width: 16 },
    { header: "buildingType", key: "buildingType", width: 12 },
    { header: "factor", key: "factor", width: 28 },
    { header: "value", key: "value", width: 12 },
    { header: "notes", key: "notes", width: 60 },
  ];
  for (const r of result.itemResults) {
    const notes = r.factorChain.notes.join("；");
    for (const factor of FACTOR_NAMES) {
      chainsSheet.addRow({
        itemId: r.itemId,
        buildingType: r.buildingType,
        factor,
        value: r.factorChain[factor] as number,
        notes,
      });
    }
  }
  chainsSheet.getRow(1).font = { bold: true };

  // ---- PriceSource sheet (only when prices is provided and has any
  //      metadata present) ----
  if (prices) {
    const presentRows = PRICE_SOURCE_ROWS.filter((r) => {
      const v = r.pick(prices);
      return v !== undefined && v !== null && v !== "";
    });
    if (presentRows.length > 0) {
      const psSheet = workbook.addWorksheet("PriceSource");
      psSheet.columns = [
        { header: "字段", key: "field", width: 24 },
        { header: "值", key: "value", width: 60 },
      ];
      for (const r of presentRows) {
        psSheet.addRow({ field: r.field, value: r.pick(prices) as string });
      }
      psSheet.getRow(1).font = { bold: true };
    }
  }

  // ---- Summary sheet ----
  const summarySheet = workbook.addWorksheet("Summary");
  summarySheet.columns = [
    { header: "项目", key: "label", width: 28 },
    { header: "值", key: "value", width: 20 },
  ];
  summarySheet.addRow({ label: "宗地总地价 (元)", value: result.totalLandPrice });
  summarySheet.addRow({ label: "计价建筑面积 (㎡)", value: result.totalChargeableGfa });
  summarySheet.addRow({ label: "综合楼面地价 (元/㎡)", value: result.averageFloorLandPrice });
  summarySheet.addRow({ label: "分项数量", value: result.itemResults.length });
  summarySheet.addRow({ label: "校验消息数量", value: result.validationMessages.length });
  summarySheet.getRow(1).font = { bold: true };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Uint8Array(buffer);
}
